import { useSelector } from 'react-redux';
import { OnboardingChrome, SlideOutSheet } from '../components';
import { selectClient } from '../model';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

import { formatCurrency } from '@shared/formatCurrency';
import { SlideContent } from '../components/SlideContent';
import { getFirstCarePhasePage } from '../util/navigationLogic';
import { OnboardingResultsAreIn, cn } from '..';
import FormattedList from '@/components/FormattedList';
import {
  formatPercent,
  formatThousands,
  CareEnvironment,
  CarePhase,
  isDurationCustom,
  areNumbersClose,
  defaultHealthcareInflation,
  isNullOrUndefined,
  appliedHealthCareInflation,
} from '@shared';
import {
  CalendarFoldIcon,
  CalendarHeartIcon,
  ChartNoAxesCombinedIcon,
  CheckCircleIcon,
  CircleDollarSignIcon,
  HeartHandshakeIcon,
  InfoIcon,
  LandmarkIcon,
  MapPinHouseIcon,
  Settings2Icon,
  TagIcon,
} from 'lucide-react';
import { useState } from 'react';
import { EditIconButton } from '@/components/EditIconButton';
import EditHealthCareInflationModal from '@client/components/EditHealthCareInflationModal';
import { HealthcareInflationCitation } from '@client/components/HealthcareInflationCitation';
import { CostDataMethodologySlideOutContent } from '@/components/LearnMoreSlideOutContent';
import { BsFillQuestionCircleFill } from 'react-icons/bs';
import { Badge } from '@client/components/ui/badge';

export function OnboardingCostFactors({ clientId }: ClientIdProps) {
  const client = useSelector(selectClient)!;
  const {
    clientFirstName,
    allPhaseCosts: { allPhaseInflatedProfessionalShareCost },
    intakeSurvey: { clientHasStartedLtc },
  } = client;

  return (
    <OnboardingChrome
      slidePage={OnboardingCostFactors}
      nextButtonLabel={`Refine ${clientFirstName}'s Cost`}
      nextSlidePage={getFirstCarePhasePage(client)}
      previousSlidePage={clientHasStartedLtc ? OnboardingResultsAreIn : undefined}
    >
      <SlideContent
        headerText={
          <>
            <span data-testid="cost-factors-header">
              {clientFirstName}&apos;s estimated{clientHasStartedLtc ? ' remaining' : ''} long-term
              care costs
            </span>
          </>
        }
      >
        <div className="mb-12 mt-4 md:mb-8 md:mt-8">
          <Badge className="ml-auto flex w-max gap-1 bg-sky-500/15 pl-1 font-medium text-sky-800 hover:bg-sky-500/15">
            <InfoIcon className="size-3.5" />
            Expand for detail
          </Badge>
          <CostFactorAccordion className="mt-2" />
          <div
            className="flex justify-between rounded-b-xl border border-gray-200 bg-gray-100 p-4"
            data-testid="cost-factors-total"
          >
            <label className="flex flex-col text-base text-gray-950 md:text-2xl">
              Estimated total costs
              <p className="whitespace-nowrap text-sm text-gray-700 md:text-lg">
                *without a funding plan
              </p>
            </label>
            <p
              className="mr-10 text-base font-semibold text-gray-950 md:text-2xl"
              data-testid="cost-factors-total-value"
            >
              {formatCurrency(allPhaseInflatedProfessionalShareCost)}
            </p>
          </div>
        </div>
      </SlideContent>
    </OnboardingChrome>
  );
}

type CostFactorAccordionProps = {
  className?: string;
};

function CostFactorAccordion({ className }: CostFactorAccordionProps) {
  const client = useSelector(selectClient)!;
  const {
    allPhaseCosts: {
      allPhaseTotalCost,
      allPhaseInflatedFamilyCareCost,
      allPhaseInflatedProfessionalShareCost,
      allPhaseCareMonthsNeeded,
      allPhaseFamilyProvidedPercent,
      allPhaseFamilyCareHoursProvided,
    },
    intakeSurvey: {
      clientIndependencePreference,
      clientFinancialSecurityPreference,
      clientFamilyInvolvementPreference,
      clientHasStartedLtc,
    },
    clientFirstName,
    clientPhasePredictedEndYears,
    phaseCosts,
    clientCalculationSettings: { healthCareInflationRate: clientHealthCareInflationRate },
    clientLocalCareEnvironmentCosts,
  } = client;

  const allPhaseInflatedCost =
    allPhaseInflatedProfessionalShareCost + allPhaseInflatedFamilyCareCost;
  const healthCareInflationContribution = allPhaseInflatedCost - allPhaseTotalCost;

  const defaultInflationRate = defaultHealthcareInflation(client);
  const appliedInflationRate = appliedHealthCareInflation(client);
  const isInflationEdited =
    !isNullOrUndefined(clientHealthCareInflationRate) &&
    !areNumbersClose(clientHealthCareInflationRate, defaultInflationRate);

  const rateLocaleType = clientLocalCareEnvironmentCosts[CareEnvironment.home].rateLocaleType;
  const appliedZipCode = clientLocalCareEnvironmentCosts[CareEnvironment.home].zipCode;


  const careDurationEdited = isDurationCustom(phaseCosts);

  const [editModalOpen, setEditModalOpen] = useState(false);

  const costFactorsData = [
    {
      id: 1,
      changeType: 'increase',
      cost: allPhaseTotalCost,
      title: `${clientHasStartedLtc ? 'Cost of Remaining Care' : 'Cost of Care'}`,
      description: `Some of ${clientFirstName}'s cost factors:`,
    },
    {
      id: 2,
      changeType: 'increase',
      cost: healthCareInflationContribution,
      title: 'Healthcare Inflation',
      description: 'The total projected cost increase is calculated using:',
    },
    {
      id: 3,
      changeType: 'decrease',
      cost: allPhaseInflatedFamilyCareCost,
      title: 'Family Caregiving',
      description: 'Family caregiving estimates based on client preferences and family structure.',
      isLast: true,
    },
  ];

  const costCareData = getCareSettingsContent(
    clientFirstName,
    rateLocaleType,
    appliedZipCode,
    allPhaseCareMonthsNeeded / 12,
    clientIndependencePreference,
    clientFinancialSecurityPreference,
    clientHasStartedLtc,
    careDurationEdited,
  );

  const healthcareInflationData = getHealthcareInflationContent(
    allPhaseTotalCost,
    Math.round(clientPhasePredictedEndYears[CarePhase.fullCare]!),
    appliedInflationRate,
    isInflationEdited,
    healthCareInflationContribution,
    setEditModalOpen,
  );

  const familyInvolvementData = getFamilyInvolvementContent(
    clientFirstName,
    clientFamilyInvolvementPreference,
    allPhaseFamilyProvidedPercent,
    allPhaseFamilyCareHoursProvided,
    allPhaseInflatedFamilyCareCost,
    rateLocaleType,
    appliedZipCode,
  );

  return (
    <>
      <Accordion
        type="single"
        collapsible
        className={cn(className)}
      >
        {costFactorsData.map(costFactor => (
          <AccordionItem
            key={costFactor.id}
            value={`item-${costFactor.id}`}
            className={cn('border-x border-b-0 border-t', {
              'rounded-t-xl': costFactor.id === 1,
              'border-t': costFactor.id === 2,
            })}
          >
            <AccordionTrigger className="border-0 p-4">
              <AccordionTriggerContent
                title={costFactor.title}
                cost={costFactor.cost}
                changeType={costFactor.changeType as 'increase' | 'decrease'}
              />
            </AccordionTrigger>
            <AccordionContent className="pb-0">
              {costFactor.id === 1 && (
                <div className="space-y-4 border-t border-gray-200 p-4">
                  {costCareData.map(({ label, value, icon, isPreferences }, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-3"
                    >
                      <div className="grid size-10 shrink-0 place-items-center rounded-lg bg-gray-100 text-gray-950 md:size-11">
                        {icon}
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-700">{label}</span>
                        {isPreferences ? (
                          <div className="mt-2 flex flex-col gap-2">
                            {value.map(el => (
                              <span className="flex items-baseline gap-1.5 text-sm text-gray-950">
                                <CheckCircleIcon className="size-3 shrink-0 translate-y-px" />
                                {el}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm font-medium text-gray-950 md:text-base">
                            {value}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {costFactor.id === 2 && (
                <>
                  <div className="space-y-4 border-t border-gray-200 p-4">
                    {healthcareInflationData.map(({ label, value, icon }, idx) => (
                      <div
                        key={idx}
                        className="flex items-start gap-3"
                      >
                        <div className="grid size-10 shrink-0 place-items-center rounded-lg bg-gray-100 text-gray-950 md:size-11">
                          {icon}
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-700">{label}</span>
                          <span className="size-sm font-medium text-gray-950 md:text-base">
                            {value}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-col gap-2 border-t border-gray-200 bg-gray-100 p-4">
                    <FootNote className="flex gap-1">
                      <p>1. </p>
                      <HealthcareInflationCitation client={client} />
                    </FootNote>
                    <FootNote className="flex gap-1">
                      <p>2. </p>
                      <p>
                        This simplified explanation is for educational purposes only and may
                        slightly overestimate the actual calculation used to estimate cost.
                      </p>
                    </FootNote>
                  </div>
                </>
              )}
              {costFactor.id === 3 && (
                <div className="space-y-4 border-t border-gray-200 p-4">
                  {familyInvolvementData.map(({ label, value, icon, isPreferences }, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-3"
                    >
                      <div className="grid size-10 shrink-0 place-items-center rounded-lg bg-gray-100 text-gray-950 md:size-11">
                        {icon}
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-700">{label}</span>
                        {isPreferences ? (
                          <div className="mt-2 flex flex-col gap-2">
                            {value.map(el => (
                              <span className="flex items-baseline gap-1.5 text-sm text-gray-950">
                                <CheckCircleIcon className="size-3 shrink-0 translate-y-px" />
                                {el}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="size-sm font-medium text-gray-950 md:text-base">
                            {value}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      <EditHealthCareInflationModal
        open={editModalOpen}
        setOpen={setEditModalOpen}
      />
    </>
  );
}

function FootNote({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('inline-block text-xs leading-snug text-gray-700', className)}>
      {children}
    </div>
  );
}

type AccordionTriggerContentProps = {
  title: string;
  cost: number;
  changeType: 'increase' | 'decrease';
};

function AccordionTriggerContent({ title, cost, changeType }: AccordionTriggerContentProps) {
  return (
    <div className="flex w-full items-center justify-between pr-4 text-base font-medium md:text-xl">
      <div className="text-gray-950">{title}</div>
      <div
        className={cn({
          'text-red-700': changeType === 'increase',
          'text-green-700': changeType === 'decrease',
        })}
      >
        {changeType === 'decrease' ? '-' : '+'}
        {formatCurrency(cost)}
      </div>
    </div>
  );
}

const getHealthcareInflationContent = (
  currentCost: number,
  projectedEndYear: number,
  inflationRate: number,
  isInflationEdited: boolean,
  healthCareInflationContribution: number,
  setEditModalOpen: (open: boolean) => void,
) => [
  {
    label: `Current cost: `,
    value: formatCurrency(currentCost),
    icon: <TagIcon className="size-4 md:size-5" />,
  },
  {
    label: `Projected care end year: `,
    value: projectedEndYear,
    icon: <CalendarHeartIcon className="size-4 md:size-5" />,
  },
  {
    label: `${isInflationEdited ? 'Edited healthcare inflation¹: ' : 'Healthcare inflation¹: '} `,
    value: (
      <span className="flex items-center">
        <span>{`${formatPercent(inflationRate, 1)} annually`}</span>
        <EditIconButton
          onClick={() => setEditModalOpen(true)}
          tooltipText="Edit healthcare inflation rate"
        />
      </span>
    ),
    icon: <LandmarkIcon className="size-4 md:size-5" />,
  },
  {
    label: 'Future projected cost²: ',
    value: formatCurrency(currentCost + healthCareInflationContribution),
    icon: <ChartNoAxesCombinedIcon className="size-4 md:size-5" />,
  },
];

const getCareSettingsContent = (
  clientFirstName: string,
  rateLocaleType: RateLocaleType,
  zipCode: string,
  ltcDurationYears: number,
  clientIndependencePreference: string,
  clientFinancialSecurityPreference: string,
  clientHasStartedLtc: boolean,
  careDurationEdited: boolean,
) => [
  {
    label: `${careDurationEdited ? 'Edited' : 'Projected'} care timeline`,
    value: ` ${formatThousands(ltcDurationYears, '', 1)} years ${clientHasStartedLtc ? 'remaining' : ''}`,
    icon: <CalendarFoldIcon className="size-4 md:size-5" />,
  },
  {
    label: 'Market rates for care in',
    value: (
      <span className="flex items-center gap-1">
        {localCostString(rateLocaleType, zipCode)}
        <SlideOutSheet
          slideOutContent={<CostDataMethodologySlideOutContent />}
          title="Cost Data"
        >
          <BsFillQuestionCircleFill className="size-3 -translate-y-0.5 text-gray-500 hover:cursor-pointer hover:text-gray-800" />
        </SlideOutSheet>
      </span>
    ),
    icon: <MapPinHouseIcon className="size-4 md:size-5" />,
  },
  {
    label: `${clientFirstName}'s preferences:`,
    value: [clientIndependencePreference, clientFinancialSecurityPreference],
    icon: <Settings2Icon className="size-4 md:size-5" />,
    isPreferences: true,
  },
];

const getFamilyInvolvementContent = (
  clientFirstName: string,
  clientFamilyInvolvementPreference: string,
  allPhaseFamilyProvidedPercent: number,
  allPhaseFamilyCareHoursProvided: number,
  allPhaseInflatedFamilyCareCost: number,
  rateLocaleType: RateLocaleType,
  zipCode: string,
) => [
  {
    label: 'Projected family caregiving: ',
    value: `${formatThousands(allPhaseFamilyCareHoursProvided, '', 0)} hours (${formatPercent(allPhaseFamilyProvidedPercent, 1)} of care needs)`,
    icon: <HeartHandshakeIcon className="size-4 md:size-5" />,
  },
  {
    label: 'Projected value of care provided by family: ',
    value: `${formatCurrency(allPhaseInflatedFamilyCareCost)}`,
    icon: <CircleDollarSignIcon className="size-4 md:size-5" />,
  },
  {
    label: 'Based on market rates for home care in ',
    value: `${localCostString(rateLocaleType, zipCode)}`,
    icon: <MapPinHouseIcon className="size-4 md:size-5" />,
  },
  {
    label: `${clientFirstName}'s preferences:`,
    value: [clientFamilyInvolvementPreference],
    icon: <Settings2Icon className="size-4 md:size-5" />,
    isPreferences: true,
  },
];

function localCostString(rateLocaleType: RateLocaleType, zipCode: string) {
  switch (rateLocaleType) {
    case 'National':
      return 'The US';
    case 'State':
      return 'Your State';
    case 'Local':
      return `Zip Code ${zipCode}`;
  }
}
